import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { configSectionsInOut, selectInOut } from 'animations-lib';
import { IFieldMeta } from '../../../../../interfaces/imetadata';
import { SortDirn, SortOption } from '../../../../../utils/fieldconfig';
import { FieldConfigChangedControllerService } from '../../../data-fields/field-config-changed-controller.service';

enum ChangeEventSrc {
  SortField,
  SortChildField,
  SortDirn,
}

@Component({
    selector: 'app-sort-options',
    templateUrl: './sort-options.component.html',
    styleUrls: ['./sort-options.component.scss', '../field-options.component.scss'],
    animations: [configSectionsInOut, selectInOut],
    standalone: false
})
export class SortOptionsComponent implements OnInit {
  @Input()
  set srcFields(srcFields: IFieldMeta[] | undefined) {
    // all the available fields
    this.getSortFields(srcFields);
  }

  @Input()
  set fieldOptions(fieldOptions: SortOption | undefined) {
    this.initFieldOptions(fieldOptions);
    this.hasChanged = false;
  }
  @Output() fieldOptionsChange = new EventEmitter<SortOption>();

  sortFields: IFieldMeta[] = []; // only the fields that can be used to sort

  expanded = false;
  sortField: IFieldMeta | undefined;
  sortChildField: IFieldMeta | undefined;
  sortDirn: SortDirn | undefined;
  hasChanged = false;

  readonly SortDirn = SortDirn;
  readonly ChangeEventSrc = ChangeEventSrc;

  constructor(private configChangedControllerService: FieldConfigChangedControllerService) {}

  ngOnInit() {
    this.hasChanged = this.configChangedControllerService.get('SortOptionsComponent');
  }

  isValid(): boolean {
    if (this.sortField === undefined || this.sortDirn === undefined) {
      return false;
    }
    if (this.sortField.type !== 'to-one') {
      return true;
    }
    return this.sortChildField !== undefined;
  }

  getSortOption(): SortOption | undefined {
    const childFieldPath = this.sortChildField?.path?.substring(this.sortField!.parent!.path.length + 1);
    return this.isValid()
      ? {
          fieldPath: this.sortField!.path,
          dirn: this.sortDirn!,
          childFieldPath: childFieldPath,
        }
      : undefined;
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  updateParent(source: ChangeEventSrc) {
    if (source === ChangeEventSrc.SortField) {
      if (this.sortField?.type === 'to-one') {
        this.sortChildField = this.findSortField(`${this.sortField?.path}.id`, this.getSortChildFields());
      } else {
        this.sortChildField = undefined;
      }
    }
    this.fieldOptionsChange.emit(this.getSortOption());
    this.configChangedControllerService.update('SortOptionsComponent', true);
    setTimeout(() => (this.hasChanged = true), 0);
  }

  getSortChildFields(): IFieldMeta[] | undefined {
    const isAssociatedField = (f: IFieldMeta) => f.type === 'to-many' || f.type === 'to-one' || f.type === 'composite';
    return this.sortField?.fields?.filter((cf) => !isAssociatedField(cf));
  }

  private initFieldOptions(fieldOptions: SortOption | undefined) {
    if (fieldOptions) {
      this.sortDirn = fieldOptions.dirn;
      this.sortField = this.findSortField(fieldOptions.fieldPath, this.sortFields);
      if (this.sortField?.type === 'to-one') {
        const childFieldPath = `${this.sortField?.parent!.path}.${fieldOptions.childFieldPath!}`;
        this.sortChildField = this.findSortField(childFieldPath, this.sortField?.fields);
      }
    }
  }

  private getSortFields(srcFields: IFieldMeta[] | undefined = []): void {
    this.sortFields = srcFields?.filter((cf) => cf.type !== 'to-many') || [];
  }

  private findSortField(fieldPath: string, searchList: IFieldMeta[] | undefined): IFieldMeta | undefined {
    const field = searchList?.find((value) => value.path === fieldPath);
    if (!field) {
      // TODO: throw an error?
      console.error(`'${fieldPath}' doesn't exist in srcFields.`);
    }
    return field;
  }
}
